import { DotLegalEmptyState, DotLegalTable, ITableHeader, ServerSideConfig } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { Checkbox, TableCell } from "@mui/material";
import React, { useState } from "react";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import EmptySvg from "../annualWheelListOverview/EmptyList.svg?react";
import NoDataSvg from "../../processingActivity/processingActivityOverview/processingActivitySharingsTab/noSharings.svg?react";
import TrialExpiredRowWrapper from "../../common/components/TrialExpiredRowWrapper";
import { ActivitySearchItem } from "./ActivitiesSearch.types";
import DynamicActivityCellRenderer from "./components/DynamicActivityCellRenderer";
import StatusTimelineBar from "./components/statusTimeline/StatusTimelineBar";
import { StatusTimelineConfig } from "./components/statusTimeline/StatusTimelineFieldConfig";
import { generateTimelineMonths } from "./components/statusTimeline/statusTimelineUtils";
import StatusTimelineDrillDownDialog from "./components/statusTimeline/StatusTimelineDrillDownDialog";
import AnnualWheelOverflowMenu from "../AnnualWheelOverflowMenu";
import ActivityResponsibleCell from "./components/ActivityResponsibleCell";
import { canEditResponsible, showsResponsibleEditor } from "./ActivitiesInlineEdit.hooks";
import { UserSelectableItem } from "../../user/User.types";

export interface IActivitiesListOverviewProps {
    activities: Array<ActivitySearchItem> | undefined;
    isLoading: boolean;
    setActivityForDeletion: (activity: ActivitySearchItem | undefined) => void;
    onOpenActivity: (annualWheelActivityId: string) => void;
    hasData: boolean;
    selectedDisplayFields: Array<string>;
    sortByColumn?: string;
    sortDirection: "asc" | "desc";
    onSortChange: (column: string) => void;
    timelineConfig?: StatusTimelineConfig;
    paginationProps: {
        calculatedPage: number;
        count: number;
        from: number;
        to: number;
        noOfPages: number;
        setPage: (pageNumber: number) => void;
        labelOf: string;
    };
    isSelectionMode?: boolean;
    selectedIds?: Set<string>;
    onCheckboxClick?: (id: string) => void;
    onSelectAllOnPage?: (pageIds: Array<string>, checked: boolean) => void;
    getHeaderCheckBoxState?: (pageIds: Array<string>) => "true" | "false" | "indeterminate";
    editingField?: string | null;
    selectableResponsibles?: Array<UserSelectableItem>;
    responsiblesLoading?: boolean;
    getResponsibleName?: (activity: ActivitySearchItem) => string;
    onResponsibleChange?: (activityId: string, responsibleId: string) => void;
}

function ActivitiesListOverview(props: IActivitiesListOverviewProps) {
    const { translateString, getChosenLanguage } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();
    const [drillDown, setDrillDown] = useState<{ activityId: string; activityName: string; monthKey: string } | undefined>();

    function getHeaderDictionary(): Record<string, string> {
        let dict: Record<string, string> = {
            id: "ID",
            activity: translateString("activity"),
            "start-date": translateString("startDate"),
            "end-date": translateString("endDate"),
            recurrence: translateString("recurrence"),
            "days-prior-to-notification": translateString("daysPriorToNotification"),
            type: translateString("type"),
            responsible: translateString("responsible"),
            assignees: translateString("assignees"),
            "approved-before-completed": translateString("approvedBeforeCompleted"),
            "business-areas": translateString("businessAreas"),
            "group-companies": translateString("groupCompanies"),
            "sub-tasks": translateString("subTasks"),
            description: translateString("description"),
            "documentation-required": translateString("documentationRequired"),
            priority: translateString("priority"),
            status: translateString("status"),
            evaluation: translateString("evaluation"),
            "external-contract-parties": translateString("externalContractParties"),
            "internal-contract-parties": translateString("internalContractParties"),
        };

        if (platformFeatures.tags && permissions.canAccessTags) {
            dict["tags"] = translateString("tags");
        }
        if (platformFeatures.annualWheelComplianceArea) {
            dict["compliance-areas"] = translateString("area");
        }
        if (permissions.contractManagementPermissions.canAccessContractsOverview) {
            dict["contracts"] = translateString("contracts");
        }

        if (permissions.frameworkPermissions.read) {
            dict["frameworks"] = translateString("frameworks");
        }

        return dict;
    }

    const headerDict = getHeaderDictionary();
    const canEditAnnualWheel = permissions.annualWheelPermission.edit;
    const canEditContracts = permissions.contractManagementPermissions.permissions.edit;

    const timelineMonths =
        props.timelineConfig && props.selectedDisplayFields.includes("status-timeline")
            ? generateTimelineMonths(props.timelineConfig, getChosenLanguage())
            : [];

    for (const tm of timelineMonths) {
        headerDict[tm.id] = tm.label;
    }

    const expandedFields = props.selectedDisplayFields.flatMap((fieldId) => {
        if (fieldId === "status-timeline") return timelineMonths.map((tm) => tm.id);
        return [fieldId];
    });

    const visibleFields = expandedFields.filter((fieldId) => headerDict[fieldId]);
    const hasActivities = props.hasData && props.activities && props.activities.length > 0;

    const headers: Array<ITableHeader<ActivitySearchItem>> = visibleFields.map((fieldId) => ({
        text: headerDict[fieldId],
        property: fieldId,
        notSortable: fieldId.startsWith("timeline:"),
        // The responsible column is pinned so that swapping its text for a select on entering edit mode does not reflow the table.
        width: fieldId.startsWith("timeline:") ? "150px" : fieldId === "responsible" ? "200px" : undefined,
    }));

    const pageIds = (props.activities ?? []).map((a) => a.id);
    const headerCheckBoxState = props.isSelectionMode && props.getHeaderCheckBoxState ? props.getHeaderCheckBoxState(pageIds) : undefined;

    const serverSideConfig: ServerSideConfig = {
        order: props.sortDirection,
        orderBy: props.sortByColumn ?? "",
        onSortChange: (columnId) => props.onSortChange(columnId),
        page: props.paginationProps.calculatedPage,
        from: props.paginationProps.from,
        to: props.paginationProps.to,
        totalPages: props.paginationProps.noOfPages,
        totalCount: props.paginationProps.count,
        onPageChange: props.paginationProps.setPage,
    };

    return (
        <React.Fragment>
            {props.isLoading || hasActivities ? (
                <DotLegalTable
                    hasOverflowMenu={!props.isSelectionMode}
                    internalScroll
                    headers={headers}
                    defaultOrderBy={"id"}
                    defaultOrder={"asc"}
                    data={props.activities ?? []}
                    isLoading={props.isLoading}
                    disableFilterAndSortAndSlicing
                    serverSide={serverSideConfig}
                    paginationLabelOf={props.paginationProps.labelOf}
                    emptyText={translateString("noActivitiesMatchesQuery")}
                    onSelectAllChanged={
                        props.isSelectionMode && props.onSelectAllOnPage ? (selected) => props.onSelectAllOnPage!(pageIds, selected.length > 0) : undefined
                    }
                    headerCheckBoxState={headerCheckBoxState}
                    renderRow={(row, index) => {
                        const canEditActivity = !props.isSelectionMode && permissions.annualWheelPermission.edit && !row.notPartOfCurrentPlan && !row.deleted;
                        return (
                            <TrialExpiredRowWrapper
                                onNonExpiredClick={() => (canEditActivity ? props.onOpenActivity(row.id) : {})}
                                key={row.id + row.month.toString() + index}
                                // A deleted row is dimmed the same way a plan locked one is, so the tooltip is what
                                // tells the two apart. The plan message wins when a row is both, since only it has an
                                // action behind the click.
                                tooltip={
                                    row.deleted && !row.notPartOfCurrentPlan
                                        ? translateString("activityIsDeleted")
                                        : translateString("annualWheelActivityLockedPlan")
                                }
                                expired={row.notPartOfCurrentPlan}
                                inactive={row.deleted}
                                showTooltip={row.deleted}
                                planUpgradeText={translateString("annualWheelActivityLockedPlan")}
                                pointerOnHover={canEditActivity}
                            >
                                {props.isSelectionMode && (
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <Checkbox checked={props.selectedIds?.has(row.id) ?? false} onChange={() => props.onCheckboxClick?.(row.id)} />
                                    </TableCell>
                                )}
                                {visibleFields.map((colId, i) => {
                                    if (colId.startsWith("timeline:")) {
                                        const monthKey = colId.replace("timeline:", "");
                                        const monthData = row.timeline?.[monthKey];
                                        return (
                                            <TableCell key={`${row.id}-tl-${i}`} sx={{ width: 150, minWidth: 150, maxWidth: 150 }}>
                                                {monthData ? (
                                                    <StatusTimelineBar
                                                        status={monthData}
                                                        onClick={() => setDrillDown({ activityId: row.id, activityName: row.name, monthKey })}
                                                    />
                                                ) : null}
                                            </TableCell>
                                        );
                                    }
                                    if (colId === props.editingField && !props.responsiblesLoading) {
                                        if (showsResponsibleEditor(row, canEditAnnualWheel, canEditContracts)) {
                                            return (
                                                <ActivityResponsibleCell
                                                    key={`${row.id}-responsible`}
                                                    selectedResponsibleId={row.responsibleId}
                                                    options={props.selectableResponsibles}
                                                    disabled={!canEditResponsible(row, canEditAnnualWheel, canEditContracts)}
                                                    onChange={(responsibleId) => props.onResponsibleChange?.(row.id, responsibleId)}
                                                />
                                            );
                                        }
                                        // The column may have been forced into the view for edit mode, in which case the
                                        // backend never sent a responsible field for this row to fall back on.
                                        if (!row.fields.some((f) => f.fieldId === colId)) {
                                            return <TableCell key={`${row.id}-responsible-readonly`}>{props.getResponsibleName?.(row)}</TableCell>;
                                        }
                                    }
                                    // Looked up by fieldId rather than by position: visibleFields is permission filtered through
                                    // headerDict while row.fields is not, so a positional walk shifts every later column when a
                                    // saved view contains a field the user cannot see.
                                    const field = row.fields.find((f) => f.fieldId === colId);
                                    return field ? (
                                        <DynamicActivityCellRenderer field={field} key={`${row.id}-field-${i}`} />
                                    ) : (
                                        <TableCell key={`${row.id}-empty-${i}`} />
                                    );
                                })}
                                {!props.isSelectionMode && (
                                    <TableCell align="right">
                                        <AnnualWheelOverflowMenu
                                            id={row.id}
                                            deleted={row.deleted}
                                            partOfCurrentPlan={!row.notPartOfCurrentPlan}
                                            isObligation={row.isObligation}
                                            onEditClick={() => props.onOpenActivity(row.id)}
                                            onDeleteClick={() => props.setActivityForDeletion(row)}
                                        />
                                    </TableCell>
                                )}
                            </TrialExpiredRowWrapper>
                        );
                    }}
                />
            ) : (
                <DotLegalEmptyState
                    icon={props.hasData ? <NoDataSvg /> : <EmptySvg />}
                    text={
                        <React.Fragment>
                            <span>{props.hasData ? translateString("noActivitiesMatchesQuery") : translateString("noActivities")}</span>
                        </React.Fragment>
                    }
                />
            )}
            {drillDown && (
                <StatusTimelineDrillDownDialog
                    activityId={drillDown.activityId}
                    activityName={drillDown.activityName}
                    monthKey={drillDown.monthKey}
                    onClose={() => setDrillDown(undefined)}
                />
            )}
        </React.Fragment>
    );
}

export default ActivitiesListOverview;
